<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Finance</div>
          <h1 class="calm__header__title">ภาพรวมการเงิน</h1>
          <div class="calm__header__sub">
            <!-- Pin the active filter to the header so the user can tell at a
                 glance which slice of data the page is currently showing. -->
            <span class="finance-active-round">
              <i class="fas fa-calendar-check"></i>
              <strong>{{ currentRoundLabel }}</strong>
            </span>
            <span class="calm__header__sep">·</span>
            <span><strong class="c-mono">{{ formatNum(totalCount) }}</strong> รายการ</span>
            <span class="calm__header__sep">·</span>
            <span><strong class="c-mono">{{ formatBaht(totalAmount) }}</strong> บาท</span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono c-mute">วันนี้ {{ todayStamp }}</span>
          </div>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ STAT CARDS ═══════════════ -->
      <div class="calm__stat-grid">
        <div class="calm__stat calm__stat--warning">
          <div class="calm__stat__label">ยังไม่สร้างวางบิล</div>
          <div class="calm__stat__value">
            {{ formatNum(statBucket(1).count) }}
            <span class="calm__stat__unit">รายการ</span>
          </div>
          <div class="calm__stat__meta">{{ formatBaht(statBucket(1).amount) }} บาท</div>
        </div>
        <div class="calm__stat calm__stat--blue">
          <div class="calm__stat__label">วางบิลแล้ว · รอชำระ</div>
          <div class="calm__stat__value">
            {{ formatNum(statBucket(2).count) }}
            <span class="calm__stat__unit">รายการ</span>
          </div>
          <div class="calm__stat__meta">{{ formatBaht(statBucket(2).amount) }} บาท</div>
        </div>
        <div class="calm__stat calm__stat--success">
          <div class="calm__stat__label">ชำระเงินแล้ว</div>
          <div class="calm__stat__value">
            {{ formatNum(statBucket(3).count) }}
            <span class="calm__stat__unit">รายการ</span>
          </div>
          <div class="calm__stat__meta">{{ formatBaht(statBucket(3).amount) }} บาท</div>
        </div>
        <div class="calm__stat">
          <div class="calm__stat__label">รวมในงวด</div>
          <div class="calm__stat__value">
            {{ formatNum(totalCount) }}
            <span class="calm__stat__unit">รายการ</span>
          </div>
          <div class="calm__stat__meta">{{ formatBaht(totalAmount) }} บาท</div>
        </div>
      </div>

      <!-- ═══════════════ CASH SUBMISSION CARD ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">การนำส่งเงินสดจากพนักงาน</div>
            <div class="calm__card__hint">ยอดเงินสดที่พนักงานเก็บมาและสถานะการนำส่ง</div>
          </div>
        </div>
        <div class="cashrow">
          <div class="cashcell cashcell--cleared">
            <div class="cashcell__label">นำส่งแล้ว</div>
            <div class="cashcell__value">{{ formatBaht(summary.cash_clear.cleared_amount) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
          <div class="cashcell cashcell--outstanding">
            <div class="cashcell__label">เงินค้างที่พนักงาน</div>
            <div class="cashcell__value">{{ formatBaht(summary.cash_clear.not_cleared_amount) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
          <div class="cashcell">
            <div class="cashcell__label">รวมเงินสด</div>
            <div class="cashcell__value">{{ formatBaht(cashTotal) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
        </div>
      </div>

      <!-- ═══════════════ QUICK PAYMENT CARD ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">รับชำระที่สาขา (Quick Payment)</div>
            <div class="calm__card__hint">ยอดที่รับชำระผ่านหน้า Quick Payment ในงวดนี้</div>
          </div>
          <router-link to="/lmg/quick-payment" class="c-btn c-btn--primary">
            <i class="fas fa-bolt mr-1"></i> ไปหน้ารับชำระ
          </router-link>
        </div>
        <div class="cashrow">
          <div class="cashcell cashcell--cleared">
            <div class="cashcell__label">จำนวนใบรับชำระ</div>
            <div class="cashcell__value">{{ formatNum(quickPay.count) }} <span class="c-mono c-mute" style="font-size:12px">ใบ</span></div>
          </div>
          <div class="cashcell">
            <div class="cashcell__label">ยอดรับชำระรวม</div>
            <div class="cashcell__value">{{ formatBaht(quickPay.amount) }} <span class="c-mono c-mute" style="font-size:12px">บาท</span></div>
          </div>
        </div>
      </div>

      <!-- ═══════════════ FILTER BAR (sticky) ═══════════════ -->
      <div class="calm__filterbar">
        <div class="calm__filter-grid">
          <div class="fld">
            <label>รอบเก็บเงิน</label>
            <el-select v-model="searchParams.dueRound" placeholder="ไม่เลือก">
              <el-option v-for="o in dueRoundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>เดือน</label>
            <el-select v-model="searchParams.dueMonth" placeholder="เดือน">
              <el-option v-for="o in monthOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>ปี</label>
            <el-select v-model="searchParams.dueYear" placeholder="ปี">
              <el-option v-for="o in yearOptions" :key="o" :label="o" :value="o"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>ตัวแทน</label>
            <el-select v-model="searchParams.personId" placeholder="ทั้งหมด" filterable>
              <el-option
                v-for="o in personOptions"
                :key="o.ID"
                :label="(o.FName || '') + ' ' + (o.LName || '')"
                :value="o.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>สถานะ</label>
            <el-select v-model="searchParams.status" placeholder="ทั้งหมด">
              <el-option v-for="o in financeStatus" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>&nbsp;</label>
            <button class="c-btn c-btn--primary" @click="search" :disabled="isLoading || isLoadingSummary">
              <i v-if="isLoading || isLoadingSummary" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-search"></i>
              ค้นหา
            </button>
          </div>
        </div>
      </div>

      <!-- ═══════════════ TABLE CARD ═══════════════ -->
      <!-- One container only: the card. The bare <table> sits inside it
           directly so we don't end up with a "card-on-card" visual where
           two rounded surfaces stack. overflow:hidden clips the table's
           corners to the card's radius. -->
      <div class="calm__card calm__card--flush" style="margin-top:16px">
        <div class="finance-table-head">
          <div>
            <div class="calm__card__title">รายละเอียด</div>
            <div class="calm__card__hint">เรียงตามวันครบกำหนด</div>
          </div>
          <div class="finance-table-toolbar">
            <input
              v-model="searchQuery"
              type="text"
              class="ed"
              placeholder="ค้นหา policy / ลูกค้า / ตัวแทน..."
              style="width:280px;max-width:100%"
            />
            <div class="calm__tfoot__per">
              <span>แสดงต่อหน้า</span>
              <el-select v-model="pagination.perPage" style="width:90px">
                <el-option v-for="n in pagination.perPageOptions" :key="n" :label="n" :value="n"></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <div class="finance-table-scroll">
          <table class="calm__table">
            <thead>
              <tr>
                <th class="marker">#</th>
                <th>ครบกำหนด</th>
                <th>ลูกค้า</th>
                <th>ตัวแทน</th>
                <th>หมายเลขกรมธรรม์</th>
                <th>สถานะ</th>
                <th class="num">เบี้ยสุทธิ</th>
                <th class="num">ยอดชำระ</th>
                <th>เงินสด</th>
              </tr>
            </thead>
            <tbody v-if="isLoading">
              <tr v-for="i in pagination.perPage" :key="'sk-' + i">
                <td colspan="9"><div class="calm__skel" style="width:100%"></div></td>
              </tr>
            </tbody>
            <tbody v-else-if="!queriedData.length">
              <tr>
                <td colspan="9">
                  <div class="calm__empty">
                    <div class="c-empty-icon"><i class="fas fa-folder-open"></i></div>
                    <div class="c-empty-title">ไม่พบรายการ</div>
                    <div class="c-empty-sub">ลองปรับเงื่อนไขด้านบนหรือล้างตัวกรอง</div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="(row, idx) in queriedData" :key="row.id">
                <td class="marker">{{ formatMarker(from + idx + 1) }}</td>
                <td class="c-mono">{{ row.DueDateShow }}</td>
                <td>
                  <div class="c-primary">{{ row.customer_fname }} {{ row.customer_lname }}</div>
                </td>
                <td>
                  <div class="c-primary">{{ row.owner_fname }} {{ row.owner_lname }}</div>
                  <div v-if="row.renew == 2" class="c-secondary">ต่ออายุ</div>
                </td>
                <td class="c-mono">
                  <a
                    v-if="row.transaction_id"
                    href="#"
                    class="tx-link"
                    @click.prevent="openTransaction(row.transaction_id)"
                    :title="'เปิดรายละเอียดในแท็บใหม่'"
                  >{{ row.policy_number || '#' + row.transaction_id }}</a>
                  <span v-else>—</span>
                </td>
                <td>
                  <span class="calm__pill" :class="pillClass(row.finance_status)">
                    {{ statusLabel(row.finance_status) }}
                  </span>
                </td>
                <td class="num">{{ formatBaht(row.amount) }}</td>
                <td class="num">{{ formatBaht(row.pay_amount) }}</td>
                <td>
                  <span v-if="row.paid_via === 1" class="calm__pill" :class="cashPillClass(row.cash_clear)">
                    {{ cashLabel(row.cash_clear) }}
                  </span>
                  <span v-else style="color:var(--c-text-mute)">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="calm__tfoot finance-table-foot" v-if="tableData.length">
          <div class="calm__tfoot__count">
            แสดง <strong>{{ from + 1 }}</strong>–<strong>{{ to }}</strong> จาก <strong>{{ total }}</strong> รายการ
          </div>
          <base-pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          ></base-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option } from "element-ui";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import { mapGetters, mapActions } from "vuex";

// localStorage key for filter persistence — coming back to this page restores filters.
const LS_KEY = "lmg.finance.filters";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    // Hydrate filters from localStorage so the user's last view is restored.
    let saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {};
    } catch (_) { saved = {}; }

    return {
      propsToSearch: ["policy_number", "customer_fname", "customer_lname", "owner_fname", "owner_lname"],
      searchParams: {
        dueRound: saved.dueRound != null ? saved.dueRound : 0,
        dueMonth: saved.dueMonth != null ? saved.dueMonth : new Date().getMonth() + 1,
        dueYear:  saved.dueYear  != null ? saved.dueYear  : new Date().getFullYear(),
        personId: saved.personId != null ? saved.personId : 0,
        status:   saved.status   != null ? saved.status   : 0,
      },
      // Snapshot of the filters that produced the data currently on screen.
      // Updated only when search() runs, so the header label reflects what
      // the user is *seeing*, not what's typed in the form.
      appliedFilters: null,
      dueRoundOptions: [
        { ID: 0, Name: "ไม่เลือก" },
        { ID: 1, Name: "รอบที่ 1 (วันที่ 15)" },
        { ID: 2, Name: "รอบที่ 2 (สิ้นเดือน)" },
      ],
      monthOptions: [
        { ID: 0, Name: "ไม่เลือก" },
        { ID: 1, Name: "มกราคม" }, { ID: 2, Name: "กุมภาพันธ์" }, { ID: 3, Name: "มีนาคม" },
        { ID: 4, Name: "เมษายน" }, { ID: 5, Name: "พฤษภาคม" }, { ID: 6, Name: "มิถุนายน" },
        { ID: 7, Name: "กรกฎาคม" }, { ID: 8, Name: "สิงหาคม" }, { ID: 9, Name: "กันยายน" },
        { ID: 10, Name: "ตุลาคม" }, { ID: 11, Name: "พฤศจิกายน" }, { ID: 12, Name: "ธันวาคม" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      tableData: "Finance/getData",
      summary: "Finance/getSummary",
      financeStatus: "Finance/getFinanceStatus",
      personTable: "Person/getData",
      isLoading: "Finance/isLoading",
      isLoadingSummary: "Finance/isLoadingSummary",
    }),
    personOptions() {
      return [{ ID: 0, FName: "ทั้งหมด", LName: "" }, ...this.personTable];
    },
    yearOptions() {
      const y = new Date().getFullYear();
      const out = [];
      for (let i = y - 2; i <= y + 1; i++) out.push(i);
      return out;
    },
    params() {
      return {
        DueRound: this.searchParams.dueRound,
        DueMonth: this.searchParams.dueMonth,
        DueYear:  this.searchParams.dueYear,
        PersonID: this.searchParams.personId === "" ? 0 : this.searchParams.personId,
        Status:   this.searchParams.status,
      };
    },
    totalCount() {
      return (this.summary.statuses || []).reduce((a, s) => a + (s.count || 0), 0);
    },
    totalAmount() {
      return (this.summary.statuses || []).reduce((a, s) => a + (s.amount || 0), 0);
    },
    cashTotal() {
      return (this.summary.cash_clear.cleared_amount || 0) + (this.summary.cash_clear.not_cleared_amount || 0);
    },
    quickPay() {
      return this.summary.quick_pay || { count: 0, amount: 0 };
    },
    todayStamp() {
      // DD/MM/YYYY per request.
      const d = new Date();
      const day = String(d.getDate()).padStart(2, "0");
      const m = String(d.getMonth() + 1).padStart(2, "0");
      return `${day}/${m}/${d.getFullYear()}`;
    },
    currentRoundLabel() {
      // Build a human label from the applied filter snapshot — falls back to
      // the live form when no search has run yet (shouldn't happen since
      // mounted() calls search() once, but defensive).
      const f = this.appliedFilters || this.searchParams;
      const monthName = (this.monthOptions.find(m => m.ID === f.dueMonth) || {}).Name;
      const monthShown = f.dueMonth > 0 && monthName ? monthName : "ทั้งปี";
      const year = f.dueYear || new Date().getFullYear();

      if (f.dueRound === 1) {
        return `รอบที่ 1 · 15/${String(f.dueMonth).padStart(2, "0")}/${year}`;
      }
      if (f.dueRound === 2) {
        // round 2 = last day of the month
        const last = f.dueMonth > 0
          ? new Date(year, f.dueMonth, 0).getDate()
          : null;
        if (last) {
          return `รอบที่ 2 · ${String(last).padStart(2, "0")}/${String(f.dueMonth).padStart(2, "0")}/${year}`;
        }
        return `รอบที่ 2 · ${monthShown} ${year}`;
      }
      // 0 or anything else = no round filter, show the period instead.
      return `ทุกรอบ · ${monthShown} ${year}`;
    },
  },
  watch: {
    searchParams: {
      deep: true,
      handler(val) {
        try { localStorage.setItem(LS_KEY, JSON.stringify(val)); } catch (_) {}
      },
    },
  },
  async mounted() {
    this.listPerson();
    await this.search();
  },
  methods: {
    ...mapActions({
      listData: "Finance/listData",
      getSummary: "Finance/getSummary",
      listPerson: "Person/listData",
    }),
    async search() {
      // Snapshot the filter state that produced this fetch so the header label
      // reports what's actually on screen (not whatever the form is showing).
      this.appliedFilters = { ...this.searchParams };
      await Promise.all([this.listData(this.params), this.getSummary(this.params)]);
    },
    statBucket(id) {
      const found = (this.summary.statuses || []).find(s => s.status === id);
      return found || { count: 0, amount: 0 };
    },
    statusLabel(s) {
      const found = this.financeStatus.find(f => f.ID === s);
      return found ? found.Name : "";
    },
    pillClass(s) {
      if (s === 1) return "calm__pill--warning";
      if (s === 2) return "calm__pill--blue";
      if (s === 3) return "calm__pill--success";
      if (s === 4) return "calm__pill--neutral";
      return "calm__pill--neutral";
    },
    cashPillClass(c) {
      if (c === 1) return "calm__pill--success";
      if (c === 2) return "calm__pill--warning";
      return "calm__pill--neutral";
    },
    cashLabel(c) {
      if (c === 1) return "นำส่งแล้ว";
      if (c === 2) return "ค้างที่พนักงาน";
      return "—";
    },
    formatBaht(n) {
      if (n == null) return "0";
      const v = Number(n);
      if (!isFinite(v)) return "0";
      return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatNum(n) {
      if (n == null) return "0";
      return Number(n).toLocaleString("en-US");
    },
    formatMarker(n) {
      return String(n).padStart(3, "0");
    },
    openTransaction(id) {
      // Open the read-only detail page in a new tab — preserves the current
      // finance filter/scroll context.
      if (!id) return;
      const route = this.$router.resolve({ name: "TransView", params: { id } });
      window.open(route.href, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
/* In-row link that opens the read-only transaction view in a new tab.
   Underline on hover so the affordance is obvious without making every cell
   look like a link. */
.tx-link {
  color: var(--c-blue-deep);
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px dashed transparent;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.tx-link:hover {
  color: var(--c-blue);
  border-bottom-color: var(--c-blue);
}

/* "Currently showing" badge — small inline pill with calm-blue tinted bg
   so the active period reads as the primary header context, ahead of the
   neutral count/amount/today metadata. */
.finance-active-round {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--c-blue-soft);
  color: var(--c-blue-deep);
  font-size: 13px;
  white-space: nowrap;
}
.finance-active-round i { font-size: 11px; }
.finance-active-round strong { font-weight: 600; }

/*
 * Table card layout: one rounded surface (the card), with a header strip,
 * the bare <table>, and the footer strip — all sharing the card's border-radius
 * via overflow:hidden. No nested table-wrap, so we don't get a "card on card"
 * doubled-surface look.
 */
.finance-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-bottom: 1px solid var(--c-border);
}
.finance-table-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.finance-table-scroll {
  overflow-x: auto;
  /* Let the <table> render flush to the card edges without its own background
     or border — the card already provides those. */
}
.finance-table-foot {
  padding: 14px 22px;
  margin-top: 0;
  border-top: 1px solid var(--c-border);
}

/* Cash submission row — three calm cells inside the card. */
.cashrow {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  background: var(--c-surface-soft);
  border: 1px solid var(--c-border);
  border-radius: 8px;
  overflow: hidden;
}
@media (max-width: 720px) {
  .cashrow { grid-template-columns: 1fr; }
}
.cashcell {
  padding: 16px 20px;
  border-right: 1px solid var(--c-border);
}
.cashcell:last-child { border-right: 0; }
.cashcell__label {
  font-size: 12px;
  color: var(--c-text-soft);
  margin-bottom: 6px;
  font-weight: 500;
}
.cashcell__value {
  font-size: 22px;
  font-weight: 600;
  color: var(--c-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.cashcell--cleared .cashcell__value { color: var(--c-success); }
.cashcell--outstanding .cashcell__value { color: var(--c-warning); }
</style>
